// src/services/imageConsentService.ts
import { logger } from '../utils/logger';
// Service for managing image sharing consent in chats and groups

import { supabase } from './supabase';

export interface ImageConsentStatus {
  enabled: boolean;
  requestedBy?: string;
  requestedAt?: string;
  canRequest: boolean; // Whether current user can request
  canApprove: boolean; // Whether current user can approve pending request
}

/**
 * Get image consent status for a 1-on-1 match
 */
export async function getMatchImageConsent(matchId: string): Promise<{
  success: boolean;
  status?: ImageConsentStatus;
  error?: string;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data: match, error } = await supabase
      .from('matches')
      .select('images_enabled, images_requested_by, images_requested_at, user1_id, user2_id')
      .eq('id', matchId)
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    if (!match) {
      return { success: false, error: 'Match not found' };
    }

    const otherUserId = match.user1_id === user.id ? match.user2_id : match.user1_id;
    const hasPendingRequest = !!match.images_requested_by;
    const requestedByCurrentUser = match.images_requested_by === user.id;
    const requestedByOtherUser = match.images_requested_by === otherUserId;

    const status: ImageConsentStatus = {
      enabled: match.images_enabled || false,
      requestedBy: match.images_requested_by || undefined,
      requestedAt: match.images_requested_at || undefined,
      canRequest: !match.images_enabled && !hasPendingRequest,
      canApprove: !match.images_enabled && requestedByOtherUser,
    };

    return { success: true, status };
  } catch (error) {
    logger.error('Error getting image consent:', error);
    return { success: false, error: 'Failed to get consent status' };
  }
}

/**
 * Request image sharing in a 1-on-1 match
 */
export async function requestMatchImageSharing(matchId: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Update match to set pending request
    const { error } = await supabase
      .from('matches')
      .update({
        images_requested_by: user.id,
        images_requested_at: new Date().toISOString(),
      })
      .eq('id', matchId)
      .is('images_requested_by', null) // Only if no pending request
      .eq('images_enabled', false); // Only if not already enabled

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error requesting image sharing:', error);
    return { success: false, error: 'Failed to request image sharing' };
  }
}

/**
 * Approve image sharing request in a 1-on-1 match
 */
export async function approveMatchImageSharing(matchId: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Enable images and clear request
    const { error } = await supabase
      .from('matches')
      .update({
        images_enabled: true,
        images_requested_by: null,
        images_requested_at: null,
      })
      .eq('id', matchId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error approving image sharing:', error);
    return { success: false, error: 'Failed to approve image sharing' };
  }
}

/**
 * Reject image sharing request in a 1-on-1 match
 */
export async function rejectMatchImageSharing(matchId: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    // Clear the pending request
    const { error } = await supabase
      .from('matches')
      .update({
        images_requested_by: null,
        images_requested_at: null,
      })
      .eq('id', matchId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error rejecting image sharing:', error);
    return { success: false, error: 'Failed to reject image sharing' };
  }
}

/**
 * Disable image sharing in a 1-on-1 match (either user can disable)
 */
export async function disableMatchImageSharing(matchId: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { error } = await supabase
      .from('matches')
      .update({
        images_enabled: false,
        images_requested_by: null,
        images_requested_at: null,
      })
      .eq('id', matchId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error disabling image sharing:', error);
    return { success: false, error: 'Failed to disable image sharing' };
  }
}

/**
 * Get image consent status for a group
 */
export async function getGroupImageConsent(groupId: string): Promise<{
  success: boolean;
  enabled?: boolean;
  error?: string;
}> {
  try {
    const { data: group, error } = await supabase
      .from('groups')
      .select('images_enabled')
      .eq('id', groupId)
      .single();

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, enabled: group?.images_enabled || false };
  } catch (error) {
    logger.error('Error getting group image consent:', error);
    return { success: false, error: 'Failed to get consent status' };
  }
}

/**
 * Toggle image sharing in a group (group leader only)
 */
export async function toggleGroupImageSharing(
  groupId: string,
  enabled: boolean
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Verify user is group leader
    const { data: group, error: checkError } = await supabase
      .from('groups')
      .select('leader_id')
      .eq('id', groupId)
      .single();

    if (checkError) {
      return { success: false, error: checkError.message };
    }

    if (group.leader_id !== user.id) {
      return { success: false, error: 'Only the group leader can change this setting' };
    }

    // Update group
    const { error: updateError } = await supabase
      .from('groups')
      .update({ images_enabled: enabled })
      .eq('id', groupId);

    if (updateError) {
      return { success: false, error: updateError.message };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error toggling group image sharing:', error);
    return { success: false, error: 'Failed to update setting' };
  }
}
